// src/components/GenericModal.js
import React, { useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';

const defaultModalRootId = 'modal-root';

const ModalOverlayStyled = styled.div`
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  background-color: rgba(0, 0, 0, 0.85) !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  width: 100vw !important;
  height: 100vh !important;
  animation: fadeIn 0.2s ease-in-out !important;
  box-sizing: border-box !important;
  margin: 0 !important;
  padding: 0 !important;
  backdrop-filter: blur(8px) !important;
  -webkit-backdrop-filter: blur(8px) !important;
  z-index: ${props => props.$zIndex || 99999};

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  ${props => props.$customOverlayStyles}
`;

const ModalContainerStyled = styled.div`
  background-color: white;
  width: 90%;
  max-width: 400px;
  border-radius: 20px;
  padding: 0; /* Defaulting to 0, specific modals can add padding or use content wrapper */
  box-sizing: border-box;
  animation: slideIn 0.3s ease-in-out;
  overflow: hidden; /* Important for rounded corners with content */

  @keyframes slideIn {
    from { transform: translateY(20px); opacity: 0.8; }
    to { transform: translateY(0); opacity: 1; }
  }

  ${props => props.$customContainerStyles}
`;

const HeaderSectionStyled = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 20px 10px; /* Default padding, can be adjusted by content */
  position: relative;
  border-bottom: ${props => props.$withBorder ? '1px solid #eee' : 'none'};
`;

const ModalTitleStyled = styled.h2`
  font-size: 24px;
  color: #000;
  text-align: left;
  font-weight: 600;
  margin: 0;
  padding: 0;
  line-height: 1.1;
`;

const CloseButtonStyled = styled.button`
  background: none;
  border: none;
  color: #666;
  font-size: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  width: 30px;
  height: 30px;
  border-radius: 50%;

  &:hover {
    background-color: #f0f0f0;
  }
`;

const ContentWrapper = styled.div`
  padding: ${props => props.$padding ?? '20px'};
  /* Default padding, can be set to 0 if children handle their own padding */
`;


const GenericModal = ({
  isOpen,
  onClose,
  title,
  children,
  modalRootId = defaultModalRootId,
  zIndex = 99999,
  hideCloseButton = false,
  customOverlayStyles,
  customContainerStyles,
  containerMaxWidth = '400px',
  disableBodyScroll = true,
  headerBorder = false,
  contentPadding = '20px', // Default content padding
}) => {
  const portalRoot = useMemo(() => {
    let root = document.getElementById(modalRootId);
    if (!root) {
      root = document.createElement('div');
      root.id = modalRootId;
      // Basic styling for the root, will be overridden by overlay if it's the direct child
      root.style.position = 'fixed';
      root.style.top = '0';
      root.style.left = '0';
      root.style.width = '100%';
      root.style.height = '100%';
      // zIndex for portalRoot itself, if it's used by multiple modals
      // The ModalOverlayStyled will have its own zIndex on top of this
      root.style.zIndex = (zIndex -1).toString();
    }
    return root;
  }, [modalRootId, zIndex]);

  useEffect(() => {
    if (!document.getElementById(modalRootId)) {
      document.body.appendChild(portalRoot);
    }
    // Ensure portalRoot is available when the component mounts and isOpen is true
    // This is more for cases where the modal might be open on initial render
    if (isOpen && !portalRoot.parentElement) {
        document.body.appendChild(portalRoot);
    }
  }, [isOpen, portalRoot, modalRootId]);

  useEffect(() => {
    if (isOpen && disableBodyScroll) {
      document.body.classList.add('modal-open');
      document.body.style.overflow = 'hidden';
      // Potentially hide other fixed elements, similar to BoosterModal
      // This needs to be configurable or handled carefully
      document.querySelectorAll('header, footer, nav, .HeaderWrapper, .CoinInfoContainer').forEach(elem => {
        if (elem && elem.id !== modalRootId && !elem.contains(portalRoot)) { // check if elem is not the modal root or its ancestor
          elem.style.setProperty('display', 'none', 'important');
        }
      });
       // Ensure the portal root itself is visible and correctly layered
      portalRoot.style.zIndex = (zIndex - 1).toString(); // The overlay will be zIndex
      portalRoot.style.position = 'fixed';
      portalRoot.style.top = '0';
      portalRoot.style.left = '0';
      portalRoot.style.width = '100vw';
      portalRoot.style.height = '100vh';

    } else if (disableBodyScroll) {
      document.body.classList.remove('modal-open');
      document.body.style.overflow = '';
      document.querySelectorAll('header, footer, nav, .HeaderWrapper, .CoinInfoContainer').forEach(elem => {
         if (elem && elem.id !== modalRootId && !elem.contains(portalRoot)) {
          elem.style.display = ''; // Reset display
         }
      });
    }

    return () => {
      // Cleanup on unmount if modal was open and disabling scroll
      if (disableBodyScroll) {
        document.body.classList.remove('modal-open');
        document.body.style.overflow = '';
        document.querySelectorAll('header, footer, nav, .HeaderWrapper, .CoinInfoContainer').forEach(elem => {
          if (elem && elem.id !== modalRootId && !elem.contains(portalRoot)) {
            elem.style.display = '';
          }
        });
      }
    };
  }, [isOpen, disableBodyScroll, portalRoot, zIndex, modalRootId]);

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <ModalOverlayStyled
      onClick={onClose}
      $zIndex={zIndex}
      $customOverlayStyles={customOverlayStyles}
      className="generic-modal-overlay" // Added for easier selection if needed
    >
      <ModalContainerStyled
        onClick={e => e.stopPropagation()}
        $customContainerStyles={customContainerStyles}
        style={{ maxWidth: containerMaxWidth }} // Allow dynamic max-width
        className="generic-modal-container" // Added for easier selection
      >
        {(title || !hideCloseButton) && (
          <HeaderSectionStyled $withBorder={headerBorder}>
            {title && <ModalTitleStyled>{title}</ModalTitleStyled>}
            {!hideCloseButton && <CloseButtonStyled onClick={onClose}>×</CloseButtonStyled>}
          </HeaderSectionStyled>
        )}
        <ContentWrapper $padding={contentPadding}>
          {children}
        </ContentWrapper>
      </ModalContainerStyled>
    </ModalOverlayStyled>,
    portalRoot
  );
};

export default GenericModal;
